import { theme } from '@traba/theme'
import { Fragment } from 'react'
import {
  Bar,
  BarChart,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import styled from 'styled-components'
import {
  FACILITY_LABOR,
  HEATMAP_CELLS,
  HEATMAP_DAYS,
  INDY_STACK,
  INDY_TUE_BRANDS,
  NETWORK_WEEK,
  TRABA_HISTORY,
  TRABA_RECOMMENDED_THIS_WEEK,
} from './data'
import {
  CHART_AXIS_PROPS,
  CHART_PALETTE,
  ChartTooltip,
  DashboardHeader,
  DiscussWithNeoButton,
  HeroCard,
  HeroVerdict,
  InlineBar,
  InsightCallout,
  MetricCard,
  MetricRow,
  SectionCard,
  SectionSubtitle,
  SectionTitle,
  ShellRoot,
  TwoColRow,
} from './DashboardShell'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCount(value: number): string {
  return value.toLocaleString('en-US')
}

/**
 * Bar scaling denominators — computed from the data so the chart doesn't
 * drift if the underlying numbers change. (Previously hardcoded as 130
 * and 130, which were correct only against the post-pass-1 state.)
 */
const MAX_FACILITY_GAP_ABS = Math.max(
  ...FACILITY_LABOR.map((f) => Math.abs(f.gapHours)),
)
const MAX_INDY_TUE_LABOR_HOURS = Math.max(
  ...INDY_TUE_BRANDS.map((b) => b.laborHours),
)

const MAX_FACILITY_ORDERS = Math.max(...FACILITY_LABOR.map((f) => f.ordersWeek))

// ---------------------------------------------------------------------------
// Heatmap (the hero visual)
//
// Cell color is interpolated from a 5-stop ramp so the Mon/Tue red blocks
// gradient smoothly into the Thu/Fri green ones. Cells round their corners
// for a softer look than a default conditional-format heatmap.
//
// Endpoint stops use theme tokens (Green80, Red60); midpoint grey uses
// Grey20. Intermediate stops are hex midpoints that don't exist in the
// palette — they're computed visually to bridge the endpoint hues.
// ---------------------------------------------------------------------------

const HEATMAP_RAMP: Array<{ gap: number; color: string }> = [
  { gap: -100, color: theme.colors.Green80 }, // deep green endpoint
  { gap: -40, color: '#A6E5C5' }, // light green midpoint
  { gap: 0, color: theme.colors.Grey20 }, // neutral grey
  { gap: 40, color: '#FFB3C4' }, // light red midpoint
  { gap: 120, color: theme.colors.Red60 }, // deep red endpoint
]

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const v = hex.replace('#', '')
  return {
    r: parseInt(v.slice(0, 2), 16),
    g: parseInt(v.slice(2, 4), 16),
    b: parseInt(v.slice(4, 6), 16),
  }
}

function rgbToHex(r: number, g: number, b: number): string {
  return `#${[r, g, b]
    .map((c) => Math.round(c).toString(16).padStart(2, '0'))
    .join('')}`
}

function interpolateColor(gap: number): string {
  const first = HEATMAP_RAMP[0]
  const last = HEATMAP_RAMP[HEATMAP_RAMP.length - 1]
  if (!first || !last) {
    return theme.colors.Grey20
  }
  if (gap <= first.gap) {
    return first.color
  }
  if (gap >= last.gap) {
    return last.color
  }
  for (let i = 0; i < HEATMAP_RAMP.length - 1; i++) {
    const a = HEATMAP_RAMP[i]
    const b = HEATMAP_RAMP[i + 1]
    if (!a || !b) {
      continue
    }
    if (gap >= a.gap && gap <= b.gap) {
      const t = (gap - a.gap) / (b.gap - a.gap)
      const ac = hexToRgb(a.color)
      const bc = hexToRgb(b.color)
      return rgbToHex(
        ac.r + (bc.r - ac.r) * t,
        ac.g + (bc.g - ac.g) * t,
        ac.b + (bc.b - ac.b) * t,
      )
    }
  }
  return HEATMAP_RAMP[2]?.color ?? theme.colors.Grey20
}

function cellTextColor(gap: number): string {
  // High-contrast text — white on the dark red/green ends, midnight blue
  // otherwise. Negative threshold tightened to -55 so cells in the -60
  // band (Indy Thu) get white text against the dark-green interpolated
  // background instead of muddy navy-on-dark-green.
  if (gap >= 75 || gap <= -55) {
    return theme.colors.White
  }
  return theme.colors.MidnightBlue
}

function formatGap(gap: number): string {
  if (gap > 0) {
    return `+${gap}h`
  }
  if (gap < 0) {
    return `${gap}h`
  }
  return '0'
}

const HeatmapShell = styled.div`
  display: grid;
  grid-template-columns: 152px repeat(10, minmax(0, 1fr));
  gap: 4px;
  font-family: ${theme.typography.fontFamily};
`

const HeatmapHeaderCell = styled.div<{ $emphasize?: boolean }>`
  padding: 10px 4px 6px;
  text-align: center;
  font-size: 10px;
  font-weight: ${({ $emphasize }) => ($emphasize ? 700 : 600)};
  color: ${({ $emphasize }) =>
    $emphasize ? theme.colors.MidnightBlue : theme.colors.Grey60};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`

const HeatmapRowLabel = styled.div`
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 600;
  color: ${theme.colors.MidnightBlue};
  display: flex;
  align-items: center;
`

const HeatmapCellBox = styled.div<{ $bg: string; $color: string }>`
  padding: 14px 4px;
  border-radius: 8px;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
  letter-spacing: -0.005em;
  transition: transform 0.15s ease;

  &:hover {
    transform: scale(1.05);
  }
`

const HeatmapLegend = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.space.sm}px;
  font-family: ${theme.typography.fontFamily};
  font-size: 11px;
  color: ${theme.colors.Grey60};
  margin-top: ${theme.space.xs}px;
`

const LegendGradient = styled.div`
  // Endpoint stops mirror HEATMAP_RAMP's theme.colors.Green80 / Grey20 /
  // Red60. Midpoint hex stops are interpolation-only midpoints (not in
  // the palette) — keep in lockstep with HEATMAP_RAMP if either changes.
  width: 200px;
  height: 12px;
  border-radius: 6px;
  background: linear-gradient(
    90deg,
    ${theme.colors.Green80} 0%,
    #a6e5c5 25%,
    ${theme.colors.Grey20} 50%,
    #ffb3c4 75%,
    ${theme.colors.Red60} 100%
  );
`

const LegendScale = styled.div`
  display: flex;
  justify-content: space-between;
  width: 200px;
  margin-top: 2px;
  font-size: 10px;
  color: ${theme.colors.Grey60};
`

// ---------------------------------------------------------------------------
// Facility list (replaces the spreadsheet table)
// ---------------------------------------------------------------------------

const FacilityList = styled.div`
  display: flex;
  flex-direction: column;
`

const FacilityListHeader = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 1fr 1.4fr 1.3fr 1fr;
  gap: ${theme.space.sm}px;
  padding: 0 ${theme.space.xs}px ${theme.space.xxs}px;
  font-family: ${theme.typography.fontFamily};
  font-size: 11px;
  font-weight: 600;
  color: ${theme.colors.Grey60};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom: 1px solid ${theme.colors.Grey20};
  margin-bottom: ${theme.space.xs}px;
`

const FacilityRow = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 1fr 1.4fr 1.3fr 1fr;
  align-items: center;
  gap: ${theme.space.sm}px;
  padding: ${theme.space.xs}px ${theme.space.xs}px;
  border-radius: 8px;

  & + & {
    margin-top: 2px;
  }
`

const FacilityNameCell = styled.div`
  font-family: ${theme.typography.fontFamily};
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.MidnightBlue};
`

const FacilityOrdersCell = styled.div`
  font-family: ${theme.typography.fontFamily};
  font-size: 13px;
  color: ${theme.colors.Grey60};

  strong {
    display: block;
    color: ${theme.colors.MidnightBlue};
    font-size: 14px;
    font-weight: 600;
  }
`

const FacilityRecCell = styled.div`
  font-family: ${theme.typography.fontFamily};
  font-size: 13px;
  color: ${theme.colors.Grey80};
`

// ---------------------------------------------------------------------------
// Tuesday brands list
// ---------------------------------------------------------------------------

const TueList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const TueRow = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 1fr 1.4fr;
  align-items: center;
  gap: ${theme.space.xs}px;
  padding: ${theme.space.xxs}px ${theme.space.xs}px;
`

const TueListHeader = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 1fr 1.4fr;
  gap: ${theme.space.xs}px;
  padding: 0 ${theme.space.xs}px ${theme.space.xxs}px;
  font-family: ${theme.typography.fontFamily};
  font-size: 11px;
  font-weight: 600;
  color: ${theme.colors.Grey60};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom: 1px solid ${theme.colors.Grey20};
  margin-bottom: ${theme.space.xxs}px;
`

const TueBrand = styled.div`
  font-family: ${theme.typography.fontFamily};
  font-size: 13px;
  font-weight: 500;
  color: ${theme.colors.MidnightBlue};

  small {
    display: block;
    color: ${theme.colors.Grey60};
    font-size: 11px;
    font-weight: 400;
  }
`

const TueOrders = styled.div`
  font-family: ${theme.typography.fontFamily};
  font-size: 13px;
  color: ${theme.colors.Grey80};
`

// ---------------------------------------------------------------------------
// Traba history hero
// ---------------------------------------------------------------------------

const HistoryRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 240px;
  gap: ${theme.space.med}px;
  align-items: center;
`

const HistoryRec = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.space.xxs}px;
  padding-left: ${theme.space.sm}px;
  border-left: 3px solid ${theme.colors.Violet60};
`

const HistoryValue = styled.div`
  font-family: ${theme.typography.fontFamily};
  font-size: 40px;
  font-weight: 700;
  color: ${theme.colors.Violet80};
  letter-spacing: -0.02em;
  line-height: 1.05;
`

const HistorySub = styled.div`
  font-family: ${theme.typography.fontFamily};
  font-size: 12px;
  color: ${theme.colors.Grey60};
  line-height: 1.45;
`

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function LaborForecastDashboard() {
  const indyDailyData = INDY_STACK.map((d) => ({
    day: d.dateLabel,
    ft: d.ftCapacityHours,
    traba: d.trabaContingentHours,
    spare: d.spareHours,
  }))

  return (
    <ShellRoot>
      <DashboardHeader
        title="Labor Forecast & Capacity"
        subtitle="14-day forward labor outlook across all 4 fulfillment centers. Updates every 4:30 AM ET from ShipHero historical velocity + per-brand productivity model + FT schedules."
        rightSlot={
          <DiscussWithNeoButton
            scriptId="labor-forecast"
            prompt="Walk me through next week's labor forecast"
          />
        }
      />

      <HeroVerdict
        tone="warning"
        eyebrow="Network labor · next week"
        headline="Indy + Atlanta are 330 hrs short Mon+Tue · Reno + Charlotte are 110 hrs long Thu+Fri"
        sub="Geography pins demand — east/midwest brands ship from Indy + Atlanta; west-coast from Reno; southeast overflow at Charlotte. No cross-facility rebalance available. 58 Traba picker/packer slots cover the front-half shortage; the back half rebalances internally."
      />

      <MetricRow>
        <MetricCard
          tone="neutral"
          label="Next-week forecast orders"
          value={formatCount(NETWORK_WEEK.openOrders)}
          sub="4 facilities · 68 brands"
        />
        <MetricCard
          tone="warning"
          label="Network labor gap"
          value={`+${NETWORK_WEEK.laborNetGapHours} hrs`}
          sub="2 short · 2 long · geography-bound"
        />
        <MetricCard
          tone="neutral"
          label="Traba slots planned"
          value={`${NETWORK_WEEK.contingentSlotsRecommended}`}
          sub={`~$${NETWORK_WEEK.contingentSlotsCostEstimate.toLocaleString('en-US')} contingent labor`}
        />
        <MetricCard
          tone="good"
          label="FT utilization (this week)"
          value={`${NETWORK_WEEK.ftUtilizationPctThisWeek}%`}
          sub="network avg · 133 picker FTE"
        />
      </MetricRow>

      <InsightCallout
        tone="warning"
        glyph="●"
        text={
          <>
            <strong>Tuesday is the heaviest single day in the network</strong> —
            14,800 Indy orders, 19 Traba slots recommended. Trailmix + Briar +
            Northwind drive ~60% of Tuesday Indy labor.
          </>
        }
      />

      <HeroCard>
        <SectionTitle>
          Network labor heatmap · next 10 working days
        </SectionTitle>
        <SectionSubtitle>
          Gap in hours per facility-day. Red = short (contingent labor
          recommended). Green = long (release FT early or batch ahead). Color
          interpolates smoothly across the scale.
        </SectionSubtitle>
        <HeatmapShell>
          <HeatmapHeaderCell />
          {HEATMAP_DAYS.map((d, i) => (
            <HeatmapHeaderCell key={d} $emphasize={i < 5}>
              {d}
            </HeatmapHeaderCell>
          ))}
          {FACILITY_LABOR.map((f) => (
            <Fragment key={f.id}>
              <HeatmapRowLabel>{f.name}</HeatmapRowLabel>
              {HEATMAP_DAYS.map((day) => {
                const cell = HEATMAP_CELLS.find(
                  (c) => c.facilityId === f.id && c.dateLabel === day,
                )
                const gap = cell?.gapHours ?? 0
                return (
                  <HeatmapCellBox
                    key={`${f.id}-${day}`}
                    $bg={interpolateColor(gap)}
                    $color={cellTextColor(gap)}
                    title={`${f.name} · ${day} · ${formatGap(gap)}`}
                  >
                    {formatGap(gap)}
                  </HeatmapCellBox>
                )
              })}
            </Fragment>
          ))}
        </HeatmapShell>
        <HeatmapLegend>
          <span>Gap scale</span>
          <div>
            <LegendGradient />
            <LegendScale>
              <span>-100h long</span>
              <span>balanced</span>
              <span>+120h short</span>
            </LegendScale>
          </div>
        </HeatmapLegend>
      </HeroCard>

      <SectionCard>
        <SectionTitle>Next-week summary by facility</SectionTitle>
        <SectionSubtitle>
          Mirrors the chat replay. Bar shows the facility's order share of the
          network week.
        </SectionSubtitle>
        <FacilityListHeader>
          <div>Facility</div>
          <div>Orders</div>
          <div>Share of network</div>
          <div>Gap</div>
          <div>Recommendation</div>
        </FacilityListHeader>
        <FacilityList>
          {FACILITY_LABOR.map((f) => {
            const sharePct = (f.ordersWeek / MAX_FACILITY_ORDERS) * 100
            const isShort = f.gapHours > 0
            const gapStr = isShort
              ? `+${f.gapHours}h short`
              : `${f.gapHours}h long`
            const gapTonePill: 'critical' | 'warning' | 'good' =
              f.status === 'critical'
                ? 'critical'
                : f.status === 'warning'
                  ? 'warning'
                  : 'good'
            return (
              <FacilityRow key={f.id}>
                <FacilityNameCell>{f.name}</FacilityNameCell>
                <FacilityOrdersCell>
                  <strong>{formatCount(f.ordersWeek)}</strong>
                  {f.laborNeededHours}h need · {f.ftCapacityHours}h FT
                </FacilityOrdersCell>
                <InlineBar
                  widthPct={sharePct}
                  label={`${Math.round((f.ordersWeek / NETWORK_WEEK.openOrders) * 100)}%`}
                  tone="neutral"
                />
                <InlineBar
                  // Scale against the network's max absolute gap (computed
                  // from FACILITY_LABOR) so bars are proportionally
                  // comparable. Tone + label sign already communicate
                  // direction; bar length communicates magnitude.
                  widthPct={Math.min(
                    100,
                    (Math.abs(f.gapHours) / MAX_FACILITY_GAP_ABS) * 100,
                  )}
                  label={gapStr}
                  tone={gapTonePill}
                  labelSide="left"
                />
                <FacilityRecCell>{f.recommendation}</FacilityRecCell>
              </FacilityRow>
            )
          })}
        </FacilityList>
      </SectionCard>

      <TwoColRow>
        <SectionCard>
          <SectionTitle>Indianapolis · 5-day breakdown</SectionTitle>
          <SectionSubtitle>
            FT capacity vs Traba contingent vs spare. Mon + Tue concentration
            from post-weekend surge + Briar roast spillover + Northwind
            west-coast wave.
          </SectionSubtitle>
          <div style={{ width: '100%', height: 280 }}>
            <ResponsiveContainer>
              <BarChart
                data={indyDailyData}
                margin={{ top: 12, right: 16, bottom: 8, left: 0 }}
                barCategoryGap="22%"
              >
                <XAxis {...CHART_AXIS_PROPS} dataKey="day" />
                <YAxis
                  {...CHART_AXIS_PROPS}
                  tickFormatter={(v: number) => `${v}h`}
                />
                <Tooltip
                  cursor={{ fill: 'transparent' }}
                  content={(props) => (
                    <ChartTooltip
                      active={props.active}
                      label={props.label as string | undefined}
                      payload={
                        props.payload
                          ? props.payload.map((p) => ({
                              name: String(p.name ?? ''),
                              value:
                                typeof p.value === 'number'
                                  ? `${p.value}h`
                                  : String(p.value),
                              color: p.color,
                            }))
                          : []
                      }
                    />
                  )}
                />
                <Bar
                  dataKey="ft"
                  stackId="cap"
                  fill={CHART_PALETTE.good}
                  name="FT capacity"
                  radius={[0, 0, 4, 4]}
                />
                <Bar
                  dataKey="traba"
                  stackId="cap"
                  fill={CHART_PALETTE.brand}
                  name="Traba contingent"
                />
                <Bar
                  dataKey="spare"
                  stackId="cap"
                  fill={theme.colors.Grey20}
                  name="Spare"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div
            style={{
              display: 'flex',
              gap: theme.space.sm,
              fontFamily: theme.typography.fontFamily,
              fontSize: 12,
              color: theme.colors.Grey60,
            }}
          >
            <span>
              <span
                style={{
                  display: 'inline-block',
                  width: 12,
                  height: 12,
                  background: CHART_PALETTE.good,
                  borderRadius: 3,
                  marginRight: 6,
                  verticalAlign: 'middle',
                }}
              />
              FT capacity
            </span>
            <span>
              <span
                style={{
                  display: 'inline-block',
                  width: 12,
                  height: 12,
                  background: CHART_PALETTE.brand,
                  borderRadius: 3,
                  marginRight: 6,
                  verticalAlign: 'middle',
                }}
              />
              Traba contingent
            </span>
            <span>
              <span
                style={{
                  display: 'inline-block',
                  width: 12,
                  height: 12,
                  background: theme.colors.Grey20,
                  borderRadius: 3,
                  marginRight: 6,
                  verticalAlign: 'middle',
                }}
              />
              Spare
            </span>
          </div>
        </SectionCard>

        <SectionCard>
          <SectionTitle>Indy Tuesday · top 5 brands by labor</SectionTitle>
          <SectionSubtitle>
            Tuesday is the heaviest day next week. Trailmix, Briar, Northwind
            drive ~60% of labor demand.
          </SectionSubtitle>
          <TueListHeader>
            <div>Brand · why</div>
            <div>Orders</div>
            <div>Labor hrs</div>
          </TueListHeader>
          <TueList>
            {INDY_TUE_BRANDS.map((b) => {
              const barPct = (b.laborHours / MAX_INDY_TUE_LABOR_HOURS) * 100
              return (
                <TueRow key={b.id}>
                  <TueBrand>
                    {b.brand}
                    <small>{b.why}</small>
                  </TueBrand>
                  <TueOrders>{formatCount(b.orders)}</TueOrders>
                  <InlineBar
                    widthPct={Math.min(100, barPct)}
                    label={`${b.laborHours}h`}
                    tone="critical"
                  />
                </TueRow>
              )
            })}
          </TueList>
        </SectionCard>
      </TwoColRow>

      <SectionCard>
        <SectionTitle>
          Traba contingent · last 8 weeks + this week's recommendation
        </SectionTitle>
        <SectionSubtitle>
          Weekly slots posted (last 8 weeks). The next-week point is highlighted
          at 58 slots — Indy 38 + Atlanta 20.
        </SectionSubtitle>
        <HistoryRow>
          <div style={{ width: '100%', height: 140 }}>
            <ResponsiveContainer>
              <LineChart
                data={[
                  ...TRABA_HISTORY,
                  { weekLabel: 'Next', slots: TRABA_RECOMMENDED_THIS_WEEK },
                ]}
                margin={{ top: 12, right: 16, bottom: 8, left: 0 }}
              >
                <XAxis {...CHART_AXIS_PROPS} dataKey="weekLabel" />
                <YAxis
                  {...CHART_AXIS_PROPS}
                  tickFormatter={(v: number) => `${v}`}
                />
                <Tooltip
                  cursor={{ stroke: theme.colors.Grey30 }}
                  content={(props) => (
                    <ChartTooltip
                      active={props.active}
                      label={props.label as string | undefined}
                      payload={
                        props.payload
                          ? props.payload.map((p) => ({
                              name: 'Slots',
                              value: String(p.value),
                              color: CHART_PALETTE.brand,
                            }))
                          : []
                      }
                    />
                  )}
                />
                <Line
                  type="monotone"
                  dataKey="slots"
                  stroke={CHART_PALETTE.brand}
                  strokeWidth={2.5}
                  dot={(props) => {
                    const { cx, cy, index } = props
                    if (cx == null || cy == null) {
                      return <g />
                    }
                    const isNext = index === TRABA_HISTORY.length
                    return (
                      <circle
                        cx={cx}
                        cy={cy}
                        r={isNext ? 7 : 3.5}
                        fill={isNext ? CHART_PALETTE.brand : theme.colors.White}
                        stroke={CHART_PALETTE.brand}
                        strokeWidth={2}
                      />
                    )
                  }}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <HistoryRec>
            <HistoryValue>{TRABA_RECOMMENDED_THIS_WEEK} slots</HistoryValue>
            <HistorySub>
              recommended for May 25–29 · Indy 38 + Atlanta 20 · ~$7,500
              contingent labor
            </HistorySub>
          </HistoryRec>
        </HistoryRow>
      </SectionCard>
    </ShellRoot>
  )
}
